import { Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormViewService } from '../form-view.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../../shared/_services/json-object-factory.service';
import {
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../../shared/utils/appkit-dropdown.util';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-s3-template-view',
    templateUrl: './s3-template-view.component.html',
    styleUrls: ['./s3-template-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class S3TemplateViewComponent implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Input() visible = false;
  @Input() rowData: any = null;
  @Input() currentTheme = 'theme-black';
  @Input() GENERAL_CONFIG: any = {};
  @Input() USER_SETTINGS: any = {};
  @Input() taxYear = '';
  private globalParams: any = {};
  private filterParams: any = {};

  @Output() closed = new EventEmitter<void>();
  @Output() refreshGrid = new EventEmitter<void>();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  /** True when the component is hosted inside CeHostDialog (opened from a grid). */
  hostedInDialog = false;
  private hasAutoInitialized = false;
  /**
   * Auto-init is deferred to a microtask so that ALL CE attribute setters
   * (`row-data`, `header-data`, `base-urls`, `filter-params`) have a chance to
   * fire before we decide between edit mode (loadTemplate) and upload-only mode
   * (getBucketRecordActions). Angular Custom Elements may process attribute
   * setters in `observedAttributes` order, not the order CeHostDialog called
   * `setAttribute()`, so a synchronous gate that fires after the first setter
   * can incorrectly fall into upload mode when row-data hasn't been parsed yet.
   * Tracked via this timer so multiple setters in the same tick coalesce into a
   * single `initModal()` call.
   */
  private pendingAutoInitTimer: any = null;

  @Input('header-data')
  set _header_data(value: string) {
    if (value == null || value === '') return;
    try {
      const header = safeJsonInput<any>(value, null, 's3-template-view.parse1');
      if (header && Object.keys(header).length > 0) {
        this.globalParams = header.globalParams
          || header.globalparams
          || header.globalService?.globalParams
          || header.globalService
          || this.globalParams;
        this.GENERAL_CONFIG = header.generalConfigData || header.generalConfig || this.GENERAL_CONFIG;
        this.USER_SETTINGS = header.userSettingsData || header.userSettings || this.USER_SETTINGS;
        this.currentTheme = this.USER_SETTINGS?.user?.selected_theme || this.currentTheme;
        this.hostedInDialog = true;
        this.tryAutoInit();
      }
    } catch (e) {
      console.log('S3TemplateView header-data input not yet stable.');
    }
  }

  @Input('base-urls')
  set _base_urls(value: string) {
    if (value == null || value === '') return;
    try {
      const urls = safeJsonInput<any>(value, null, 's3-template-view.parse2');
      if (urls && Object.keys(urls).length > 0) {
        this.GENERAL_CONFIG = {
          ...(this.GENERAL_CONFIG || {}),
          base_url: urls.api ?? this.GENERAL_CONFIG?.base_url,
          admin_api_url: urls.admin ?? this.GENERAL_CONFIG?.admin_api_url,
          custom_api_url: urls.custom ?? this.GENERAL_CONFIG?.custom_api_url,
          bulk_pdf_engine_api_url: urls.bulkPdfEngine ?? this.GENERAL_CONFIG?.bulk_pdf_engine_api_url,
          pdf_engine_api_url: urls.pdfEngine ?? this.GENERAL_CONFIG?.pdf_engine_api_url,
          efile_api_url: urls.efile ?? this.GENERAL_CONFIG?.efile_api_url,
        };
        this.hostedInDialog = true;
        this.tryAutoInit();
      }
    } catch (e) {
      console.log('S3TemplateView base-urls input not yet stable.');
    }
  }

  @Input('row-data')
  set _row_data(value: string) {
    if (value == null || value === '') return;
    try {
      const data = safeJsonInput<any>(value, null, 's3-template-view.parse3');
      this.rowData = data && Object.keys(data).length > 0 ? data : null;
      this.hostedInDialog = true;
      this.tryAutoInit();
    } catch (e) {
      console.log('S3TemplateView row-data input not yet stable.');
    }
  }

  /**
   * Backend modal-name (from `Screen Modal` config), forwarded by
   * `CeHostDialogComponent` as the `modal-name` attribute. The same CE
   * (`gtw-s3-template-view-ce`) backs two registered modals:
   *
   *   - `s3TemplateView`     — large dialog (92vw) for the row-click
   *                            "View PDF" action. rowData carries
   *                            TAX_YEAR / IRS_FORM_NO / FILE_KEY and the
   *                            modal must call `getFormForS3Template`.
   *   - `s3TemplateView-sm`  — compact dialog (700px) for the toolbar
   *                            "Upload Template" action. Legacy passes
   *                            `rowData: null`, so the controller skips
   *                            `loadTemplate()` and only loads the form
   *                            dropdown via `getBucketRecordActions`.
   *                            `getFormForS3Template` MUST NOT fire.
   *
   * The grid's generic `prepareDialogData()` can't tell the two apart on
   * its own: if the user has selected a grid row and then clicks the
   * "Upload Template" toolbar action, the batchSelect entry is forwarded
   * as `rowData`, which would otherwise push the modal into view mode and
   * trigger an unwanted `getFormForS3Template` call. Capturing the modal
   * name here lets us authoritatively force upload-only behaviour for
   * the `-sm` variant regardless of what rowData was inferred.
   */
  modalName: string = '';
  @Input('modal-name')
  set _modal_name(value: string) {
    if (value == null) return;
    this.modalName = String(value);
    this.hostedInDialog = true;
    this.tryAutoInit();
  }

  @Input('filter-params')
  set _filter_params(value: string) {
    if (value == null || value === '') return;
    try {
      const params = safeJsonInput<any>(value, null, 's3-template-view.parse4');
      if (params && Object.keys(params).length > 0) {
        this.filterParams = params;
        this.taxYear = String(params.tax_year ?? this.taxYear ?? '');
        this.hostedInDialog = true;
        this.tryAutoInit();
      }
    } catch (e) {
      console.log('S3TemplateView filter-params input not yet stable.');
    }
  }

  /**
   * Returns true when the hosting modal-screen is the upload-only
   * variant (`s3TemplateView-sm`). Used by `initModal()` to skip the
   * `getFormForS3Template` PDF fetch in upload mode even if rowData
   * leaked in from a selected grid row.
   */
  private isUploadOnlyModal(): boolean {
    return this.modalName === 's3TemplateView-sm';
  }

  private tryAutoInit(): void {
    if (this.hasAutoInitialized || !this.hostedInDialog) return;
    if (this.pendingAutoInitTimer != null) return;

    this.pendingAutoInitTimer = setTimeout(() => {
      this.pendingAutoInitTimer = null;
      if (this.hasAutoInitialized) return;

      const baseReady =
        !!this.GENERAL_CONFIG?.base_url ||
        !!this.GENERAL_CONFIG?.bulk_pdf_engine_api_url;
      if (!baseReady) return;

      this.hasAutoInitialized = true;
      this.visible = true;
      console.log('[S3TemplateView] auto-init firing', {
        modalName: this.modalName,
        isUploadOnlyModal: this.isUploadOnlyModal(),
        hasRowData: !!this.rowData,
        rowKeys: this.rowData ? Object.keys(this.rowData) : [],
        bulkPdfUrl: this.GENERAL_CONFIG?.bulk_pdf_engine_api_url,
        baseUrl: this.GENERAL_CONFIG?.base_url,
      });
      this.initModal();
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.pendingAutoInitTimer != null) {
      clearTimeout(this.pendingAutoInitTimer);
      this.pendingAutoInitTimer = null;
    }
  }

  url: SafeResourceUrl | null = null;
  loadingForm = false;
  loadingDD = false;
  newUpload = false;

  showDetails = true;
  showComments = false;
  comment = '';
  pdfFile: File | null = null;

  details: any = null;
  allcomments: any[] = [];

  formlist: any = { selected: [], name: 'forms', values: [] };
  /** Selected row for upload form (PrimeNG `p-dropdown`); `value` is IRS form id string. */
  selectedForm: { name: string; value: string } | null = null;
  selectedFormItem: any = null;
  formDropdownItems: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private formViewService: FormViewService,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService
  ) {}

  ngOnChanges(): void {
    // When hosted inside CeHostDialog the lifecycle is owned by tryAutoInit().
    // Letting ngOnChanges also call initModal() would re-fire `getFormForS3Template`
    // (and `getAllComments`) for every CE attribute setter that runs after the
    // initial auto-init.
    if (this.hostedInDialog) return;
    if (this.visible) {
      this.initModal();
    }
  }

  initModal(): void {
    // Legacy parity (`formViewCtrl.js#S3TemplateView` L1693-L1703):
    //
    //     if (!rowData) {
    //         vm.newUpload = true;
    //         ...
    //         getBucketRecordActions();
    //         return;                  // <-- early return, no loadTemplate()
    //     }
    //     loadTemplate();              // calls /getFormForS3Template
    //
    // Upload mode must never call `getFormForS3Template`. We treat the
    // modal as upload-only when EITHER:
    //   (a) the hosting Screen Modal config is `s3TemplateView-sm`
    //       (`isUploadOnlyModal()`) — the authoritative signal, since
    //       the same CE serves both modal screens and the grid's
    //       generic dispatcher may have leaked a selected row into
    //       `rowData` even for the toolbar Upload Template action; or
    //   (b) no rowData was supplied at all (direct
    //       `[rowData]="null"` binding from the parent component).
    if (this.isUploadOnlyModal() || !this.rowData) {
      // Drop any rowData that leaked in from a selected grid row so the
      // upload form's `uploadData` fallback (`{TAX_YEAR, IRS_FORM_NO,
      // FILE_KEY: ''}` built in `upload()`) is used instead of trying
      // to overwrite an existing template.
      if (this.isUploadOnlyModal()) {
        this.rowData = null;
      }
      console.log('[S3TemplateView] entering newUpload mode', {
        modalName: this.modalName,
        reason: this.isUploadOnlyModal() ? 's3TemplateView-sm' : 'no-rowData',
      });
      this.newUpload = true;
      this.showDetails = true;
      this.showComments = true;
      this.comment = 'Initial file upload';
      this.selectedForm = null;
      this.getBucketRecordActions();
      return;
    }
    console.log('[S3TemplateView] entering edit mode, loading template for', {
      TAX_YEAR: this.rowData.TAX_YEAR,
      IRS_FORM_NO: this.rowData.IRS_FORM_NO,
      FILE_KEY: this.rowData.FILE_KEY,
    });
    // PDF view: no toolbar — show comments by default.
    this.showComments = true;
    this.loadTemplate();
  }

  private loadTemplate(): void {
    this.loadingForm = true;
    this.showDetails = false;
    this.comment = '';
    this.url = null;
    this.cdr.detectChanges();

    const bulkPdfUrl = this.GENERAL_CONFIG?.bulk_pdf_engine_api_url || '';
    console.log('[S3TemplateView] calling getFormForS3Template', {
      bulkPdfUrl,
      rowData: this.rowData,
    });

    this.formViewService.getFormTemplate(
      bulkPdfUrl, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: ArrayBuffer) => {
        this.loadingForm = false;
        if (data.byteLength > 0) {
          const blob = new Blob([data], { type: 'application/pdf' });
          this.url = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
        }
        this.getAllComments();
        this.cdr.detectChanges();
      },
      () => {
        this.loadingForm = false;
        this.getAllComments();
        this.cdr.detectChanges();
      }
    );
  }

  private getBucketRecordActions(): void {
    this.loadingDD = true;
    this.cdr.detectChanges();

    const url = `${this.GENERAL_CONFIG?.base_url || '/gtw'}/loadJsonObject`;
    this.jsonObjectFactory.getJSONObj6(
      url,
      { action_key: '32687', action_code: 'sikgs1' },
      { globalParams: this.globalParams },
      this.filterParams,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: any) => {
          this.loadingDD = false;
          if (res?.jsonObject) {
            this.formlist.values = res.jsonObject.map((v: any) => ({ name: v.FORM, value: v.FORM }));
            this.refreshFormDropdownItems();
          }
          this.selectedForm = null;
          this.cdr.detectChanges();
        },
        () => { this.loadingDD = false; this.cdr.detectChanges(); }
      );
  }

  private getAllComments(): void {
    if (!this.rowData) return;

    const context = {
      ...(this.globalParams || {}),
      ...(this.filterParams || {}),
    };

    const valueFor = (key: string, fallback: any = ''): string => {
      const value = context[key] ?? fallback;
      return value == null ? '' : String(value);
    };

    const url = `${this.GENERAL_CONFIG?.base_url || '/gtw'}/loadJsonObject`;
    this.jsonObjectFactory.getJSONObj6(
      url,
      {
        action_code: '3rsj53',
        activity_key: valueFor('activity_key', this.rowData.ACTIVITY_KEY),
        irs_form_no: String(this.rowData.IRS_FORM_NO || ''),
        is_issue_key_enabled: valueFor('is_issue_key_enabled', this.rowData.IS_ISSUE_KEY_ENABLED || 'N'),
        is_locked: valueFor('is_locked', this.rowData.IS_LOCKED || 'N'),
        issue_id: valueFor('issue_id', this.rowData.ISSUE_ID),
        issue_id_for_tx_save: valueFor('issue_id_for_tx_save', this.rowData.ISSUE_ID_FOR_TX_SAVE),
        issue_short_desc: valueFor('issue_short_desc', this.rowData.ISSUE_SHORT_DESC),
        jcd_key: valueFor('jcd_key', this.rowData.JCD_KEY),
        jcd_ta_key: valueFor('jcd_ta_key', this.rowData.JCD_TA_KEY),
        scenario: valueFor('scenario', this.rowData.SCENARIO),
        scenarioCode: valueFor('scenarioCode', this.rowData.SCENARIO_CODE),
        screen_key: valueFor('screen_key', this.rowData.SCREEN_KEY),
        screen_type: valueFor('screen_type', this.rowData.SCREEN_TYPE || 'grid'),
        sso_id: this.USER_SETTINGS?.user?.sso_id || valueFor('sso_id'),
        tax_year: String(this.rowData.TAX_YEAR || valueFor('tax_year')),
      },
      { globalParams: this.globalParams },
      this.filterParams,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: any) => {
          this.allcomments = res?.jsonObject || [];
          this.cdr.detectChanges();
        },
        () => {}
      );
  }

  upload(): void {
    if (!this.pdfFile) return;

    // upload() is now only reachable from the upload-only body
    // (`newUpload === true`). The view-mode body's inline replace-pdf
    // form was removed for strict legacy parity (PDF view and upload
    // view must not coexist in the same modal), so the legacy
    // `if (!vm.newUpload) loadTemplate()` branch is no longer needed.
    const uploadData = this.rowData || {
      TAX_YEAR: this.taxYear,
      IRS_FORM_NO: this.selectedForm?.value || '',
      FILE_KEY: ''
    };

    this.formViewService.uploadPdf(
      this.GENERAL_CONFIG?.bulk_pdf_engine_api_url || '',
      uploadData.TAX_YEAR, uploadData.IRS_FORM_NO,
      uploadData.FILE_KEY || '', this.comment, this.pdfFile
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.gtwSnackbarService.openSnackBar('File Uploaded Successfully', 'gtw-snackbar--green');
        this.refreshGrid.emit();
        if (this.hostedInDialog) {
          // Close dialog with `action: 'refresh'` so `grid.directive` reloads data.
          // Do NOT call `close()` here — it emits `cancel` and overwrites the dialog result.
          this.requestGridUpdate.emit('refresh');
        } else {
          this.closed.emit();
        }
      },
      () => {
        this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
      }
    );
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pdfFile = input.files?.[0] || null;
    this.cdr.detectChanges();
  }

  private refreshFormDropdownItems(): void {
    this.formDropdownItems = mapToAppkitDropdownItems(this.formlist.values, 'name', 'value');
    this.selectedFormItem = this.formDropdownItems.find(
      (item) => item.value === this.selectedForm?.value
    ) || null;
  }

  onFormDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedFormItem = selected;
    this.selectedForm = selected ? { name: selected.label, value: selected.value } : null;
    this.cdr.detectChanges();
  }

  onFormSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedFormItem = selected;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  close(): void {
    this.closed.emit();
    if (this.hostedInDialog) {
      this.requestGridUpdate.emit('cancel');
    }
  }
}
